<template>
  <BaseModal
    v-if="isShowing"
    color="dark-grey"
    rounded
    class="delete-user-dialog"
  >
    <h1 class="delete-user-dialog__title">Are you sure?</h1>
    <p class="delete-user-dialog__message">
      {{ getMessage() }}
      <template v-if="onlyAdminAvailable">
        <br />
        <br />
        Since there are no other users available to reassign this role, as the
        Admin, you will be assigned this role.
        <br />
        <br />
        If you would like to reassign this role to someone else, please create a
        new user.
      </template>
    </p>
    <template v-if="!onlyAdminAvailable">
      <FormSelect
        v-for="pocReportType in user.report_type_contacts"
        :key="`delete-user-dialog__poc-report-type-${pocReportType.id}-new-contact`"
        :id="`delete-user-dialog__poc-report-type-${pocReportType.id}-new-contact`"
        :label-text="getPocLabelText(pocReportType)"
        placeholder-text="Select user"
        :options="getPocOptionsByReportTypeAccess(pocReportType)"
        row
        width-split="1/3"
        @update:modelValue="setSelectedPocReassignments($event, pocReportType)"
      />
      <FormSelect
        v-for="dataReportType in user.report_type_data_contacts"
        :key="`delete-user-dialog__data-report-type-${dataReportType.id}-new-contact`"
        :id="`delete-user-dialog__data-report-type-${dataReportType.id}-new-contact`"
        :label-text="getDataLabelText(dataReportType)"
        placeholder-text="Select user"
        :options="getPocOptionsByReportTypeAccess(dataReportType)"
        row
        width-split="1/3"
        @update:modelValue="
          setSelectedDataReassignments($event, dataReportType)
        "
      />
    </template>

    <div class="delete-user-dialog__buttons">
      <BaseButton
        color="danger"
        :disabled="userCannotBeDeleted"
        class="delete-user-dialog__button"
        @click="__confirm()"
      >
        {{ onlyAdminAvailable ? 'DELETE' : 'DELETE & REASSIGN' }}
      </BaseButton>
      <BaseButton
        color="charcoal"
        class="delete-user-dialog__button"
        @click="__cancel()"
      >
        CANCEL
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script>
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseModal from '~/components/base/BaseModal.vue';
  import FormSelect from '~/components/form/FormSelect.vue';

  export default {
    name: 'DeleteUserDialog',
    components: {
      BaseButton,
      BaseModal,
      FormSelect,
    },
    data: () => ({
      onlyAdminAvailable: false,
      isShowing: false,
      authUser: null,
      user: null,
      resolvePromise: null,
      rejectPromise: null,
      userList: [],
      pocReportTypes: [],
      dataReportTypes: [],
      numberOfPocReportTypes: 0,
      numberOfDataReportTypes: 0,
      selectedPocReassignments: [],
      selectedDataReassignments: [],
    }),
    computed: {
      userCannotBeDeleted() {
        return (
          !this.onlyAdminAvailable &&
          (this.selectedPocReassignments.length !==
            this.numberOfPocReportTypes ||
            this.selectedDataReassignments.length !==
              this.numberOfDataReportTypes)
        );
      },
    },
    methods: {
      async prompt({ authUser, user }) {
        this.authUser = authUser;
        this.user = user;
        this.pocReportTypes = user.report_type_contacts.map(
          (reportType) => reportType.name
        );
        this.dataReportTypes = user.report_type_data_contacts.map(
          (reportType) => reportType.name
        );
        this.numberOfPocReportTypes = this.pocReportTypes.length;
        this.numberOfDataReportTypes = this.dataReportTypes.length;
        await this.getUserList();

        const pocReportTypeIds = user.report_type_contacts.map(
          (reportType) => reportType.id
        );
        const hasMoreThanOneContactPoc = this.userList.some((user) => {
          if (user.id === this.authUser.id) return false;
          return user.consulting_report_types.find((reportType) => {
            return pocReportTypeIds.includes(reportType.id);
          });
        });
        const dataReportTypeIds = user.report_type_data_contacts.map(
          (reportType) => reportType.id
        );
        const hasMoreThanOneContactData = this.userList.some((user) => {
          if (user.id === this.authUser.id) return false;
          return user.consulting_report_types.find((reportType) => {
            return dataReportTypeIds.includes(reportType.id);
          });
        });

        this.onlyAdminAvailable =
          !hasMoreThanOneContactPoc && !hasMoreThanOneContactData;
        this.isShowing = true;

        return new Promise((resolve, reject) => {
          this.resolvePromise = resolve;
          this.rejectPromise = reject;
        });
      },
      __confirm() {
        if (this.userCannotBeDeleted) return;

        this.$emit('delete-user', {
          userId: this.user.id,
          pocReassignments: this.selectedPocReassignments,
          dataReassignments: this.selectedDataReassignments,
        });

        this.resetForm();
        this.isShowing = false;
        this.resolvePromise(true);
      },
      __reject() {
        this.resetForm();
        this.isShowing = false;
        this.rejectPromise(true);
      },
      __cancel() {
        this.resetForm();
        this.isShowing = false;
        this.resolvePromise(false);
      },
      getPocLabelText(reportType) {
        if (
          this.numberOfPocReportTypes === 1 &&
          this.numberOfDataReportTypes === 0
        ) {
          return 'Reassign role:';
        } else if (
          this.numberOfPocReportTypes === 1 &&
          this.numberOfDataReportTypes === 1 &&
          this.pocReportTypes[0] === this.dataReportTypes[0]
        ) {
          return 'Reassign role for Primary Point of Contact:';
        } else if (
          this.numberOfPocReportTypes > 1 &&
          this.numberOfDataReportTypes === 0
        ) {
          return `${reportType.name} - Reassign role:`;
        }
        return `${reportType.name} - Reassign role for Primary Point of Contact:`;
      },
      getDataLabelText(reportType) {
        if (
          this.numberOfPocReportTypes === 0 &&
          this.numberOfDataReportTypes === 1
        ) {
          return 'Reassign role:';
        } else if (
          this.numberOfPocReportTypes === 1 &&
          this.numberOfDataReportTypes === 1 &&
          this.pocReportTypes[0] === this.dataReportTypes[0]
        ) {
          return 'Reassign role for Primary Data Submission Contact:';
        } else if (
          this.numberOfPocReportTypes === 0 &&
          this.numberOfDataReportTypes > 1
        ) {
          return `${reportType.name} - Reassign role:`;
        }
        return `${reportType.name} - Reassign role for Primary Data Submission Contact:`;
      },
      getMessage() {
        let message = `The user, ${this.user.name.toUpperCase()}, is assigned to the role PRIMARY POINT OF CONTACT and PRIMARY DATA SUBMISSION CONTACT for multiple reports.`;

        if (
          this.numberOfPocReportTypes === 1 &&
          this.numberOfDataReportTypes === 0
        ) {
          message = `The user, ${this.user.name.toUpperCase()}, is assigned to the role PRIMARY POINT OF CONTACT for ${this.pocReportTypes[0]}.`;
        } else if (
          this.numberOfPocReportTypes === 0 &&
          this.numberOfDataReportTypes === 1
        ) {
          message = `The user, ${this.user.name.toUpperCase()}, is assigned to the role PRIMARY DATA SUBMISSION CONTACT for ${this.dataReportTypes[0]}.`;
        } else if (
          this.numberOfPocReportTypes === 1 &&
          this.numberOfDataReportTypes === 1 &&
          this.pocReportTypes[0] === this.dataReportTypes[0]
        ) {
          message = `The user, ${this.user.name.toUpperCase()}, is assigned to the role PRIMARY POINT OF CONTACT for ${this.dataReportTypes[0]} and PRIMARY DATA SUBMISSION CONTACT for ${this.dataReportTypes[0]}.`;
        } else if (
          this.numberOfPocReportTypes > 1 &&
          this.numberOfDataReportTypes === 0
        ) {
          message = `The user, ${this.user.name.toUpperCase()}, is assigned to the role PRIMARY POINT OF CONTACT for multiple reports.`;
        } else if (
          this.numberOfPocReportTypes === 0 &&
          this.numberOfDataReportTypes === 1
        ) {
          message = `The user, ${this.user.name.toUpperCase()}, is assigned to the role PRIMARY DATA SUBMISSION CONTACT for multiple reports.`;
        }

        if (!this.onlyAdminAvailable)
          message +=
            ' This will need to be changed before you can delete this user.';

        return message;
      },
      async getUserList() {
        try {
          const { data } = await axios.get('/company-admin/user/list');
          this.userList = data.filter((user) => user.id !== this.user.id);
        } catch (error) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      getPocOptionsByReportTypeAccess(reportType) {
        return this.userList
          .filter((user) => {
            return user.consulting_report_types.find(
              (reportTypeContact) => reportTypeContact.id === reportType.id
            );
          })
          .map((user) => ({
            value: user.id,
            label: user.name,
          }));
      },
      setSelectedPocReassignments(selected, reportType) {
        this.selectedPocReassignments = this.selectedPocReassignments.filter(
          (reassignment) => reassignment.reportTypeId !== reportType.id
        );
        this.selectedPocReassignments.push({
          reportTypeId: reportType.id,
          userId: selected,
        });
      },
      setSelectedDataReassignments(selected, reportType) {
        this.selectedDataReassignments = this.selectedDataReassignments.filter(
          (reassignment) => reassignment.reportTypeId !== reportType.id
        );
        this.selectedDataReassignments.push({
          reportTypeId: reportType.id,
          userId: selected,
        });
      },
      resetForm() {
        this.authUser = null;
        this.user = null;
        this.userList = [];
        this.pocReportTypes = [];
        this.dataReportTypes = [];
        this.numberOfPocReportTypes = 0;
        this.numberOfDataReportTypes = 0;
        this.selectedPocReassignments = [];
        this.selectedDataReassignments = [];
      },
    },
  };
</script>

<style lang="scss" scoped>
  .delete-user-dialog {
    &__title {
      margin: 0;
      padding: 0;
      width: 100%;
      text-align: center;
    }
    &__message {
      margin: 0;
      padding: 32px 0;
      color: var(--itr-blue);
    }
    &__buttons {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      margin-top: 16px;
      gap: 16px;
    }
    &__button {
      width: 100%;
      max-width: 250px;
      padding: 4px 32px;
      font-size: 14px;
    }
  }
</style>
